import { useSyncExternalStore } from 'react';
import { api } from '../api/client';
import { token } from '../auth/session';
import { translate } from '../i18n/i18n';

export type MediaEntry = {
  term: string;
  learnerId: string | null;
  imageId: string | null;
  audioId: string | null;
  updatedAt: string;
};

type Resolved = { image?: string; audio?: string; learnerSpecific: boolean };

const EMOJI = /\p{Extended_Pictographic}(️|‍\p{Extended_Pictographic})*️?/gu;

export function formatMb(bytes: number): string {
  const mbs = bytes / (1024 * 1024);
  return translate('{n} MB', { n: mbs < 1 ? mbs.toFixed(1) : Math.round(mbs) });
}

export function termOf(text: string): string {
  return text.replace(EMOJI, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

let entries = new Map<string, Resolved>();
let loadedFor: string | null | undefined;
let version = 0;
const listeners = new Set<() => void>();
const objectUrls = new Map<string, string>();

function emit() {
  version++;
  listeners.forEach((l) => l());
}

async function blobUrl(id: string): Promise<string | undefined> {
  const cached = objectUrls.get(id);
  if (cached) return cached;
  const t = await token();
  const res = await fetch(`/api/media/blob/${id}`, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'omit' });
  if (!res.ok) return undefined;
  const url = URL.createObjectURL(await res.blob());
  objectUrls.set(id, url);
  return url;
}

export const mediaBlobUrl = (id: string) => blobUrl(id);

async function resolve(list: MediaEntry[]) {
  const next = new Map<string, Resolved>();
  const sorted = [...list].sort((a, b) => Number(!!a.learnerId) - Number(!!b.learnerId));
  for (const e of sorted) {
    const prev = next.get(e.term) ?? { learnerSpecific: false };
    const image = e.imageId ? await blobUrl(e.imageId) : undefined;
    const audio = e.audioId ? await blobUrl(e.audioId) : undefined;
    next.set(e.term, {
      image: image ?? prev.image,
      audio: audio ?? prev.audio,
      learnerSpecific: prev.learnerSpecific || !!e.learnerId,
    });
  }
  entries = next;
  emit();
}

export async function loadMedia(learnerId: string | null, force = false) {
  if (!force && loadedFor === learnerId) return;
  loadedFor = learnerId;
  try {
    const list = await api<MediaEntry[]>(`/media${learnerId ? `?learnerId=${encodeURIComponent(learnerId)}` : ''}`, { token: true });
    await resolve(list ?? []);
  } catch {
    entries = new Map();
    emit();
  }
}

export function customFor(text: string): Resolved | undefined {
  return entries.get(termOf(text));
}

export function useMediaVersion(): number {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l); },
    () => version,
  );
}

async function send(kind: 'image' | 'audio', term: string, file: Blob, learnerId: string | null) {
  const form = new FormData();
  form.append('term', term);
  if (learnerId) form.append('learnerId', learnerId);
  const ext = file.type.split('/')[1]?.split(';')[0] ?? 'bin';
  form.append('file', file, `${kind}.${ext}`);
  const t = await token();
  const res = await fetch('/api/media/' + kind, {
    method: 'POST',
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    body: form,
    credentials: 'omit',
  });
  if (!res.ok) {
    let message = translate('Could not save ({status})', { status: res.status });
    try { message = (await res.json()).message ?? message; } catch { message = translate('Could not save ({status})', { status: res.status }); }
    throw new Error(message);
  }
  await loadMedia(loadedFor ?? null, true);
}

export const saveImage = (term: string, file: Blob, learnerId: string | null) => send('image', termOf(term), file, learnerId);
export const saveAudio = (term: string, file: Blob, learnerId: string | null) => send('audio', termOf(term), file, learnerId);

export async function removeMedia(kind: 'image' | 'audio', term: string, learnerId: string | null) {
  const q = new URLSearchParams({ term: termOf(term) });
  if (learnerId) q.set('learnerId', learnerId);
  await api<void>(`/media/${kind}?${q}`, { method: 'DELETE', token: true });
  await loadMedia(loadedFor ?? null, true);
}

export async function shrinkImage(file: File, max = 900): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((ok, fail) => canvas.toBlob((b) => (b ? ok(b) : fail(new Error(translate('Could not read that picture')))), 'image/jpeg', 0.85));
}

let playing: HTMLAudioElement | null = null;

export function playCustom(text: string): Promise<void> | null {
  const audio = customFor(text)?.audio;
  if (!audio) return null;
  playing?.pause();
  const el = new Audio(audio);
  playing = el;
  return new Promise((done) => {
    el.onended = () => done();
    el.onerror = () => done();
    void el.play().catch(() => done());
  });
}

export function stopCustom() {
  playing?.pause();
  playing = null;
}
